import { concatLatestFrom } from '@ngrx/operators';
import {
  Action,
  createAction,
  createFeatureSelector,
  createReducer,
  createSelector,
  on,
  props,
  Store,
} from '@ngrx/store';
import { inject, Injectable, Injector } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import {
  catchError,
  EMPTY,
  filter,
  from,
  map,
  mergeMap,
  Observable,
  of,
  switchMap,
  takeUntil,
} from 'rxjs';
import { deleteToken, getToken, isSupported, Messaging } from '@angular/fire/messaging';
import Swal from 'sweetalert2';
import { environment } from '@environments/environment';
import { ApiService } from '@services/common/api.service';
import { authenticationActions, selectAuthentication } from '@redux/authentication.redux';
import { Database, orderByChild, query, ref, onValue } from '@angular/fire/database';
import type { Notification } from '@models/community/notification.model';

export interface NotificationsState {
  isSupported: boolean;
  permissionGranted: boolean;
  permissionRequested: boolean;
  firebaseToken: string | null;
  //
  loaded: boolean;
  unreadTotal: number;
  total: number;
  //
  notifications: Notification[];
  unread: Notification[];
  incoming: Notification[];
}

export const initialNotificationsState: NotificationsState = {
  isSupported: false,
  permissionGranted: false,
  permissionRequested: false,
  firebaseToken: null,
  //
  loaded: false,
  unreadTotal: 0,
  total: 0,

  //
  notifications: [],
  unread: [],
  incoming: [],
};

export const notificationsStateKey = 'NotificationsState';

export const notificationsActions = {
  isSupported: createAction(`[${notificationsStateKey}]IsSupported`),
  isSupportedSuccess: createAction(
    `[${notificationsStateKey}]IsSupportedSuccess`,
    props<{ supported: boolean }>(),
  ),
  permissionGranted: createAction(
    `[${notificationsStateKey}]PermissionGranted`,
    props<{ permissionGranted: boolean }>(),
  ),
  permissionRequested: createAction(`[${notificationsStateKey}]PermissionRequested`),
  firebaseToken: createAction(
    `[${notificationsStateKey}]FirebaseToken`,
    props<{ firebaseToken: string | null }>(),
  ),
  //
  snapshot: createAction(
    `[${notificationsStateKey}]Snapshot`,
    props<{ snapshot: Notification[] }>(),
  ),
  notifications: createAction(`[${notificationsStateKey}]Notifications`),
  notificationsSuccess: createAction(
    `[${notificationsStateKey}]NotificationsSuccess`,
    props<{
      notifications: Notification[];
      incoming: Notification[];
    }>(),
  ),
  //
  asRead: createAction(`[${notificationsStateKey}]AsRead`, props<{ model: Notification }>()),
};

const selectNotificationsFeature = createFeatureSelector<NotificationsState>(notificationsStateKey);

export const selectNotifications = {
  isSupported: createSelector(
    selectNotificationsFeature,
    (state: NotificationsState) => state.isSupported,
  ),
  permissionGranted: createSelector(
    selectNotificationsFeature,
    (state: NotificationsState) => state.permissionGranted,
  ),
  permissionRequested: createSelector(
    selectNotificationsFeature,
    (state: NotificationsState) => state.permissionRequested,
  ),
  //
  loaded: createSelector(selectNotificationsFeature, (state: NotificationsState) => state.loaded),
  total: createSelector(selectNotificationsFeature, (state: NotificationsState) => state.total),
  unreadTotal: createSelector(
    selectNotificationsFeature,
    (state: NotificationsState) => state.unreadTotal,
  ),
  //
  notifications: createSelector(
    selectNotificationsFeature,
    (state: NotificationsState) => state.notifications,
  ),
  unread: createSelector(selectNotificationsFeature, (state: NotificationsState) => state.unread),
  incoming: createSelector(
    selectNotificationsFeature,
    (state: NotificationsState) => state.incoming,
  ),
};

export const notificationsReducer = createReducer(
  initialNotificationsState,
  on(
    notificationsActions.isSupportedSuccess,
    (state, { supported }): NotificationsState => ({
      ...state,
      isSupported: supported,
    }),
  ),
  on(
    notificationsActions.permissionGranted,
    (state, { permissionGranted }): NotificationsState => ({
      ...state,
      permissionGranted,
      permissionRequested: true,
    }),
  ),
  //

  on(
    notificationsActions.notificationsSuccess,
    (state, { notifications, incoming }): NotificationsState => {
      return {
        ...state,
        notifications,
        incoming,
        unread: notifications.filter((x) => !x.readInd),
        loaded: true,
        total: notifications.length,
        unreadTotal: notifications.filter((x) => !x.readInd).length,
      };
    },
  ),
);

@Injectable()
export class NotificationsEffects {
  private actions$ = inject(Actions);
  private store = inject(Store);
  private injector = inject(Injector);
  private api = inject(ApiService);
  private database = inject(Database);

  isSupported$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(notificationsActions.isSupported),
      mergeMap(() => {
        return from(isSupported()).pipe(
          map((supported) => {
            //            console.log('supported', supported);
            return notificationsActions.isSupportedSuccess({ supported: supported as boolean });
          }),
          catchError((error) => {
            console.error('Error verificando soporte de Firebase Messaging:', error);
            return of(notificationsActions.isSupportedSuccess({ supported: false }));
          }),
        );
      }),
    );
  });

  requestPermission$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(notificationsActions.isSupportedSuccess),
      filter(({ supported }) => supported),
      concatLatestFrom(() => this.store.select(selectNotifications.permissionRequested)),
      filter(([, permissionRequested]) => !permissionRequested),
      mergeMap(() =>
        from(Notification.requestPermission()).pipe(
          map((permission) => {
            if (permission === 'granted') {
              return notificationsActions.permissionGranted({ permissionGranted: true });
            } else {
              this.requestPermission();
              return notificationsActions.permissionGranted({ permissionGranted: false });
            }
          }),
          catchError((error) => {
            console.error('Error al solicitar permiso de notificación:', error);
            return of(notificationsActions.permissionGranted({ permissionGranted: false }));
          }),
        ),
      ),
    );
  });

  permissionGranted$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(notificationsActions.permissionGranted),
      concatLatestFrom(() => [
        this.store.select(selectAuthentication.selectIsAuthenticated),
        this.store.select(selectAuthentication.selectUser),
      ]),
      filter(
        ([{ permissionGranted }, isAuthenticated, user]) =>
          permissionGranted && isAuthenticated && !!user,
      ),
      switchMap(([, , user]) => {
        const messaging = this.injector.get(Messaging);

        // Wait for Service Worker to be active before getting token
        return from(this.waitForServiceWorkerActive())
          .pipe(
            switchMap(() => {
              return from(
                getToken(messaging, {
                  vapidKey: environment.firebaseConfig.vapidKey,
                }).catch((error) => {
                  console.error('Error obteniendo el token FCM:', error);
                  return null;
                }),
              );
            }),
          )
          .pipe(
            mergeMap((firebaseToken) => {
              if (firebaseToken) {
                const hash = localStorage.getItem('hash');
                if (hash === firebaseToken) {
                  return of(notificationsActions.firebaseToken({ firebaseToken }));
                } else {
                  return this.api
                    .post(`devices`, { deviceToken: firebaseToken, userId: user!.id })
                    .pipe(
                      map(() => {
                        localStorage.setItem('hash', firebaseToken);
                        return notificationsActions.firebaseToken({ firebaseToken });
                      }),
                      catchError(() => {
                        return of(notificationsActions.firebaseToken({ firebaseToken }));
                      }),
                    );
                }
              }
              return of(notificationsActions.firebaseToken({ firebaseToken }));
            }),
            catchError(() => {
              return of(notificationsActions.firebaseToken({ firebaseToken: null }));
            }),
          );
      }),
    );
  });

  logoutNotifications$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(authenticationActions.logout),
        concatLatestFrom(() => [
          this.store.select(selectNotifications.isSupported),
          this.store.select(selectNotifications.permissionGranted),
        ]),
        filter(([, isSupported, permissionGranted]) => isSupported && permissionGranted),
        switchMap(() => {
          const messaging = this.injector.get(Messaging);
          return from(deleteToken(messaging)).pipe(
            map(() => {
              return EMPTY;
            }),
            catchError((error) => {
              console.error('Error removing Firebase token:', error);
              return EMPTY;
            }),
          );
        }),
      );
    },
    { dispatch: false },
  );

  notifications$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(notificationsActions.notifications),
      concatLatestFrom(() => [
        this.store.select(selectAuthentication.selectIsAuthenticated),
        this.store.select(selectAuthentication.selectUser),
      ]),
      filter(([, isAuthenticated, user]) => isAuthenticated && !!user),
      switchMap(([, , user]) => {
        const path = `${environment.notifications}/${user!.id}`;
        const reference = ref(this.database, path);
        const orderQuery = query(reference, orderByChild('timestamp'));

        return new Observable<Action>((observer) => {
          const unsubscribe = onValue(
            orderQuery,
            (snapshot) => {
              const values = snapshot.val();
              const notifications = (values ? Object.values(values) : []) as Notification[];
              observer.next(notificationsActions.snapshot({ snapshot: notifications.reverse() }));

              console.log(notifications);
            },
            {
              onlyOnce: false,
            },
          );

          return () => unsubscribe();
        }).pipe(
          takeUntil(this.actions$.pipe(ofType(authenticationActions.logout))),
          catchError((error) => {
            console.error('Error al obtener notificaciones en tiempo real:', error);
            return EMPTY;
          }),
        );
      }),
    );
  });

  snapshot$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(notificationsActions.snapshot),
      concatLatestFrom(() => [
        this.store.select(selectAuthentication.selectIsAuthenticated),
        this.store.select(selectNotifications.loaded),
        this.store.select(selectNotifications.notifications),
        this.store.select(selectAuthentication.selectUser),
      ]),
      filter(([, isAuthenticated, , , user]) => isAuthenticated && !!user),
      switchMap(([{ snapshot }, , loaded, collection]) => {
        const notified = collection.map((c: Notification) => c.id); // Usa map directamente
        const incoming: Notification[] = loaded
          ? snapshot.filter((x) => !notified.includes(x.id))
          : [];

        return of(
          notificationsActions.notificationsSuccess({
            notifications: snapshot,
            incoming,
          }),
        );
      }),
    );
  });

  notificationsSuccess$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(notificationsActions.notificationsSuccess),
        concatLatestFrom(() => [
          this.store.select(selectAuthentication.selectIsAuthenticated),
          this.store.select(selectNotifications.incoming),
        ]),
        filter(([, isAuthenticated, incoming]) => isAuthenticated && incoming.length > 0),
        map(([, , incoming]) => {
          incoming.forEach((notification) => {
            Swal.fire({
              position: 'bottom-end',
              //icon: 'success',
              title: notification.content.title,
              text: notification.content.body,
              showConfirmButton: false,
              timer: 7000,
              backdrop: false,
            }).then();
          });
        }),
      );
    },
    { dispatch: false },
  );

  asRead$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(notificationsActions.asRead),
        concatLatestFrom(() => [this.store.select(selectAuthentication.selectIsAuthenticated)]),
        filter(([, isAuthenticated]) => isAuthenticated),

        switchMap(([{ model }]) => {
          return this.api
            .put<Notification>(`users/notifications/${model.id}`, {
              readInd: 1,
            })
            .pipe(
              map(() => {
                return of();
              }),
              catchError(() => {
                return of();
              }),
            );
        }),
      );
    },
    { dispatch: false },
  );

  requestPermission() {
    Swal.fire({
      title: 'Notification Permissions Required',
      html: `
        <p>You need to enable notifications in your browser settings.</p>
        <p>Follow these steps:</p>
        <ol class="text-start">
          <li>Click on the lock icon <i class="bx bx bx-slider-alt font-size-20"></i> in the address bar.</li>
          <li>Select <strong>Site settings</strong>.</li>
          <li>In the Permissions section, find <strong>Notifications</strong>.</li>
          <li>Set the notifications to <strong>Allow</strong>.</li>
          <li>Reload the page after making these changes.</li>
        </ol>
      `,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#0a58ca',
      cancelButtonColor: '#c12323',
      confirmButtonText: 'Request Again',
      cancelButtonText: 'Close',
    }).then((result) => {
      if (result.isConfirmed) {
        this.store.dispatch(notificationsActions.isSupported());
      }
    });
  }

  /**
   * Wait for Service Worker to be active AND Firebase Messaging to be ready
   */
  private async waitForServiceWorkerActive(): Promise<void> {
    if (!('serviceWorker' in navigator)) {
      return Promise.reject('Service Worker not supported');
    }

    try {
      // Get current registrations
      const registrations = await navigator.serviceWorker.getRegistrations();

      // Find our Firebase messaging service worker
      const firebaseSW = registrations.find(
        (reg) =>
          reg.scope.includes('firebase-messaging-sw.js') ||
          reg.scope === window.location.origin + '/',
      );

      if (!firebaseSW) {
        return Promise.reject('Firebase Service Worker not found');
      }

      // Wait for Service Worker to be active
      if (!firebaseSW.active) {
        await this.waitForSWActivation(firebaseSW);
      }

      // Additional wait for Firebase Messaging to be fully initialized
      await this.waitForFirebaseMessagingReady();
    } catch (error) {
      console.error('Notifications: Error waiting for Service Worker:', error);
      return Promise.reject(error);
    }
  }

  /**
   * Wait for Service Worker to become active
   */
  private async waitForSWActivation(firebaseSW: ServiceWorkerRegistration): Promise<void> {
    return new Promise((resolve, reject) => {
      const timeout = setTimeout(() => {
        reject('Service Worker activation timeout');
      }, 10000);

      firebaseSW.addEventListener('statechange', () => {
        if (firebaseSW.active) {
          clearTimeout(timeout);
          resolve();
        }
      });

      if (firebaseSW.installing) {
        // Service Worker is installing, wait for it
      } else if (firebaseSW.waiting) {
        firebaseSW.waiting.postMessage({ action: 'skipWaiting' });
      }
    });
  }

  /**
   * Wait for Firebase Messaging to be fully initialized in Service Worker
   */
  private async waitForFirebaseMessagingReady(): Promise<void> {
    return new Promise((resolve, reject) => {
      const timeout = setTimeout(() => {
        reject('Firebase Messaging initialization timeout');
      }, 15000); // 15 second timeout for Firebase init

      // Try to get token with retry logic
      const tryGetToken = async (attempt: number = 1): Promise<void> => {
        try {
          const messaging = this.injector.get(Messaging);

          // Try to get token to test if Firebase Messaging is ready
          const token = await getToken(messaging, {
            vapidKey: environment.firebaseConfig.vapidKey,
          });

          if (token) {
            clearTimeout(timeout);
            resolve();
            return;
          }

          // If no token but no error, Firebase might still be initializing
          if (attempt < 5) {
            setTimeout(() => tryGetToken(attempt + 1), 1000);
          } else {
            clearTimeout(timeout);
            resolve();
          }
        } catch (error) {
          if (attempt < 5) {
            setTimeout(() => tryGetToken(attempt + 1), 1000);
          } else {
            clearTimeout(timeout);
            reject(error);
          }
        }
      };

      // Start testing after a short delay to let Firebase initialize
      setTimeout(() => tryGetToken(), 500);
    });
  }
}
